import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { IdentifierInterceptor } from '../interceptors/identifier.interceptor';

/**
 * Service for managing HTTP requests with automatic identifier injection
 * Demonstrates how the interceptor works with actual HTTP calls
 */
@Injectable({
  providedIn: 'root'
})
export class HttpIdentifierService {
  
  private readonly baseUrl = 'https://api.scottishpower.com'; // Replace with your actual API base URL
  private userIdSubject = new BehaviorSubject<string | null>(null);
  
  /**
   * Observable stream of the current user ID
   */
  public readonly userId$ = this.userIdSubject.asObservable();

  constructor(private http: HttpClient) {
    // Initialize user ID from storage if available
    const storedUserId = localStorage.getItem('sp-user-id');
    if (storedUserId) {
      this.userIdSubject.next(storedUserId);
    }
  }

  /**
   * Example method: Search for addresses (demonstrates interceptor in action)
   * @param postcode - The postcode to search for
   * @returns Observable of address search results
   */
  searchAddresses(postcode: string): Observable<any> {
    const url = `${this.baseUrl}/addresses/search`;
    const params = { postcode };
 
    return this.http.get(url, { params });
  }

  /**
   * Example method: Get meter information (demonstrates interceptor in action)
   * @param addressId - The address identifier
   * @returns Observable of meter information
   */
  getMeterInfo(addressId: string): Observable<any> {
    const url = `${this.baseUrl}/addresses/${addressId}/meters`;
    
    return this.http.get(url);
  }

  /**
   * Example method: Submit quote request (demonstrates interceptor in action)
   * @param quoteData - The quote request data
   * @returns Observable of quote submission result
   */
  submitQuote(quoteData: any): Observable<any> {
    const url = `${this.baseUrl}/quotes`;
    
    return this.http.post(url, quoteData);
  }

  /**
   * Example method: Update user preferences (demonstrates interceptor in action)
   * @param preferences - The user preferences to update
   * @returns Observable of update result
   */
  updateUserPreferences(preferences: any): Observable<any> {
    const url = `${this.baseUrl}/user/preferences`;

    return this.http.put(url, preferences);
  }

  /**
   * Gets the current session information including identifiers
   * @returns Object containing session details
   */
  getSessionInfo(): {
    sessionId: string | null;
    userId: string | null;
    applicationId: string;
    applicationVersion: string;
  } {
    return {
      sessionId: sessionStorage.getItem('sp-session-id'),
      userId: localStorage.getItem('sp-user-id'),
      applicationId: 'scottish-power-business-portal',
      applicationVersion: '1.0.0'
    };
  }

  /**
   * Logs current session information to console (useful for debugging)
   */
  logSessionInfo(): void {
    const sessionInfo = this.getSessionInfo();
  }
}
